import { FC, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { NetworkStatus } from '@apollo/client';
import { Box, Center, Stack } from '@chakra-ui/react';

import {
  TeamMembersRequestStatus,
  TeamMembersRequestType,
} from '__generated__/GQL';
import useCompanyContext from 'common/hooks/useCompanyContext';
import useMainBankAccountId from 'common/hooks/useMainBankAccountId';
import useRequestsUpdatedSubscription from 'common/hooks/useTeamRequestsUpdatedSubscription';
import Button from 'components/_core/Button';
import ErrorCard from 'components/ErrorCard';
import Loader from 'components/Loader';
import SearchBar from 'components/SearchBar';
import { logEvent } from 'features/Analytics/analytics';
import * as Layout from 'layouts/ContentLayout';
import useTheme from 'theme/hooks/useTheme';

import * as routes from '../../routes';
import { RequestsRouteParams } from '../../routes';
import BankTransferRequestSidebar from './components/BankTransferRequestSidebar';
import { ExpenseClaimRequestSidebar } from './components/ExpenseClaimRequestSidebar';
import FilterBar from './components/FilterBar';
import NoFilteredRequestsView from './components/NoFilteredRequestsView';
import NoRequestView from './components/NoRequestView';
import PaymentCardRequestSidebar from './components/PaymentCardRequestSidebar';
import TeamRequestsList from './components/TeamRequestsList';
import {
  isBankTransferRequest,
  isExpenseClaimRequest,
  isPaymentCardRequest,
  TeamRequestsDocument,
  useRequests,
} from './hooks/useRequests';
import useRequestsCreatedSubscription from './hooks/useRequestsCreatedSubscription';
import locales from './locales';
import { RequestsFilterState } from './types';
import { RequestsFilterStateToStatus } from './utils';

const CARD_REQUEST_TYPENAMES = [
  'PhysicalPaymentCardOrderRequest',
  'VirtualBudgetCardOrderRequest',
  'VirtualPaymentCardOrderRequest',
];

const Requests: FC = () => {
  const history = useHistory();
  const theme = useTheme();
  const { requestId, urlFilterState } = useParams<RequestsRouteParams>();
  let filterState = urlFilterState;
  if (!Object.values(RequestsFilterState).includes(filterState)) {
    filterState = RequestsFilterState.Pending;
    history.replace(routes.getStatePath(filterState));
  }
  const { companyProfileId } = useCompanyContext();
  const { mainBankAccountId, ...bankAccountQuery } = useMainBankAccountId();
  const { requests, ...teamRequestsQuery } = useRequests(
    RequestsFilterStateToStatus[filterState],
  );

  const [query, setQuery] = useState('');

  const selectedRequest = useMemo(
    () =>
      requests.find((request) => request.teamMembersRequestId === requestId),
    [requests, requestId],
  );

  useRequestsCreatedSubscription({
    onData: ({ client }) => {
      client.refetchQueries({
        include: [TeamRequestsDocument],
      });
    },
  });

  useRequestsUpdatedSubscription({
    onData: ({ client, data }) => {
      if (data.data && selectedRequest) {
        if (
          data.data.teamMembersRequestsUpdated.teamMembersRequestId !==
            selectedRequest.teamMembersRequestId ||
          (CARD_REQUEST_TYPENAMES.includes(
            data.data.teamMembersRequestsUpdated.__typename,
          ) &&
            data.data.teamMembersRequestsUpdated.status ===
              TeamMembersRequestStatus.Completed)
        ) {
          client.cache.evict({
            fieldName: 'teamMembersRequests',
            id: client.cache.identify({
              __typename: 'Company',
              companyProfileId,
            }),
          });
          client.cache.gc();
          history.replace(routes.getStatePath(filterState));
        }
      }
    },
  });

  if (bankAccountQuery.loading) {
    return <Loader />;
  }

  if (
    teamRequestsQuery.error ||
    bankAccountQuery.error ||
    !mainBankAccountId ||
    !companyProfileId
  ) {
    return (
      <Center height="100%">
        <ErrorCard />
      </Center>
    );
  }

  const onRequestClick = (id: string) => {
    const request = requests.find(
      ({ teamMembersRequestId }) => teamMembersRequestId === id,
    );

    if (request && isPaymentCardRequest(request)) {
      logEvent({
        name: 'PaymentCard Request Opened',
        properties: {
          cardType:
            request.type === TeamMembersRequestType.PhysicalPaymentCardOrder
              ? 'physical'
              : 'virtual',
        },
        usePrefix: false,
      });
    }
  };

  const isOnPendingFilter = filterState === RequestsFilterState.Pending;

  const cleanPendingRequests = () => {
    if (
      isOnPendingFilter &&
      selectedRequest &&
      selectedRequest.status !== TeamMembersRequestStatus.Pending
    ) {
      teamRequestsQuery.client.cache.evict({
        fieldName: 'teamMembersRequests',
        id: teamRequestsQuery.client.cache.identify({
          __typename: 'Company',
          companyProfileId,
        }),
      });
      teamRequestsQuery.client.cache.gc();
    }
  };

  const closeSidebar = () => {
    cleanPendingRequests();
    history.push(routes.getStatePath(filterState));
  };

  const filteredRequests = requests.filter((request) => {
    if (!request.requester) {
      return false;
    }

    return [
      request.__typename,
      request.requester.firstName,
      request.requester.lastName,
    ].some((str) => str.toLowerCase().includes(query.toLowerCase()));
  });

  const handleOnStatusChange = (): void => {
    cleanPendingRequests();
  };

  const isLoadingMore =
    teamRequestsQuery.networkStatus === NetworkStatus.fetchMore;

  return (
    <Layout.Root>
      <Layout.Body
        size="md"
        sx={{
          container: 'body / inline-size',
        }}
      >
        <Layout.Header
          description={locales.description}
          title={locales.title}
        />

        <Stack
          flexDirection="column"
          gap="space-16"
          justifyContent="space-between"
          paddingBottom="space-40"
          sx={{
            [`@container body (min-width: ${theme.breakpoints.md})`]: {
              flexDirection: 'row',
            },
          }}
          width="100%"
        >
          <FilterBar
            currentFilterState={filterState}
            onStatusChange={handleOnStatusChange}
          />

          <Box
            sx={{
              [`@container body (min-width: ${theme.breakpoints.md})`]: {
                width: '308px',
              },
            }}
            width="100%"
          >
            <SearchBar
              isDisabled={!requests.length}
              onChange={setQuery}
              value={query}
            />
          </Box>
        </Stack>

        {teamRequestsQuery.loading && !isLoadingMore ? (
          <Loader />
        ) : (
          <>
            {requests.length === 0 ? (
              <NoRequestView requestState={filterState} />
            ) : null}

            {requests.length > 0 && filteredRequests.length === 0 ? (
              <NoFilteredRequestsView />
            ) : null}

            {filteredRequests.length > 0 ? (
              <>
                <TeamRequestsList
                  onRequestClick={onRequestClick}
                  requests={filteredRequests}
                  selectedRequest={selectedRequest}
                />
                {teamRequestsQuery.pageInfo?.hasNextPage ? (
                  <Button
                    alignSelf="center"
                    isLoading={isLoadingMore}
                    marginTop="space-34"
                    onClick={teamRequestsQuery.loadMore}
                    variant="secondary"
                  >
                    {locales.loadMore}
                  </Button>
                ) : null}
              </>
            ) : null}
          </>
        )}
      </Layout.Body>

      {selectedRequest ? (
        <>
          {isPaymentCardRequest(selectedRequest) ? (
            <Layout.Sidebar isScrollable>
              <PaymentCardRequestSidebar
                bankAccountId={mainBankAccountId}
                companyProfileId={companyProfileId}
                isOnPendingFilter={isOnPendingFilter}
                key={selectedRequest.teamMembersRequestId}
                onClose={closeSidebar}
                request={selectedRequest}
              />
            </Layout.Sidebar>
          ) : null}
          {isBankTransferRequest(selectedRequest) ? (
            <>
              <Layout.Sidebar isScrollable>
                <BankTransferRequestSidebar
                  companyProfileId={companyProfileId}
                  isOnPendingFilter={isOnPendingFilter}
                  key={selectedRequest.teamMembersRequestId}
                  onClose={closeSidebar}
                  requestId={selectedRequest.teamMembersRequestId}
                />
              </Layout.Sidebar>
            </>
          ) : null}
          {isExpenseClaimRequest(selectedRequest) ? (
            <>
              <Layout.Sidebar isScrollable>
                <ExpenseClaimRequestSidebar
                  companyProfileId={companyProfileId}
                  isOnPendingFilter={isOnPendingFilter}
                  key={selectedRequest.teamMembersRequestId}
                  onClose={closeSidebar}
                  requestId={selectedRequest.teamMembersRequestId}
                />
              </Layout.Sidebar>
            </>
          ) : null}
        </>
      ) : null}
    </Layout.Root>
  );
};

export default Requests;
